/**
 * Billing & Plans (OutlierOS /billing replica, docs/legacy-ui/03 §11): H1 → usage meters → Monthly/Annual toggle →
 * plan cards → Manage subscription. Changed (DASHBOARD_PARITY §3.2): plans, prices and features come from the
 * database (INR for India), the current plan is marked, the browser count is real, and there is no trial banner
 * (decision 14). Payments run on the payment provider's checkout and customer portal.
 */
import { webAnalyticsAttribution, webAnalyticsGoal } from '../analytics';
import { date as fmtDate } from '../format';
import { useEffect, useState } from 'react';
import { Check, CreditCard, Loader2, X } from 'lucide-react';
import { Button, ErrorState, Pill, Segmented, Spinner, cn, toast } from '@vw/ui';
import { api } from '../api';
import { navigate, useLocation } from '../router';
import { useSession } from '../session';

interface Limits { transcriptionCreditsPerMonth: number; profileScansPerDay: number; reelsPerScan: number; pairedBrowsers: number; exportRowLimit: number | null; compare: boolean; mcp: boolean; agentScansPerHour: number; collections: number | null; collectionItems: number }
interface PlanRow { slug: string; name: string; description: string; limits: Limits | null; currency: string; monthlyCents: number; yearlyCents: number; purchasable: { month: boolean; year: boolean } }
interface BillingInfo {
  plans: PlanRow[];
  current: { plan: string; planName: string; source: string };
  subscription: { plan: string; status: string; interval: 'month' | 'year'; renewsAt: number | null; cancelsAtPeriodEnd: boolean; currency: string; portal: boolean } | null;
  usage: { credits: { used: number; allowance: number; available: number; periodStart: number; resetsAt: number }; browsers: { used: number; max: number }; scansToday: { used: number; max: number } };
}

const money = (cents: number, currency: string) => (cents / 100).toLocaleString(currency === 'INR' ? 'en-IN' : 'en-US', { style: 'currency', currency, maximumFractionDigits: cents % 100 ? 2 : 0 });
const date = (ms: number | null) => (ms ? fmtDate(ms) : '');

function features(l: Limits | null): Array<[string, boolean]> {
  if (!l) return [];
  return [
    [`${l.transcriptionCreditsPerMonth.toLocaleString()} transcription credits / month`, true],
    [`${l.profileScansPerDay.toLocaleString()} profile scans / day, up to ${l.reelsPerScan.toLocaleString()} reels each`, true],
    [`${l.pairedBrowsers} connected browser${l.pairedBrowsers === 1 ? '' : 's'}`, true],
    [l.exportRowLimit === null ? 'Full exports (CSV, Excel, Notion, JSON)' : `Exports up to ${l.exportRowLimit} rows`, true],
    [l.collections === null ? `Unlimited collections, ${l.collectionItems.toLocaleString()} reels each` : `${l.collections} collections, ${l.collectionItems} reels each`, true],
    ['Compare creators', l.compare],
    ['AI connectors (Claude, ChatGPT, Cursor)', l.mcp],
    [l.agentScansPerHour ? `Scans from AI apps (${l.agentScansPerHour} / hour)` : 'Scans from AI apps', l.agentScansPerHour > 0],
  ];
}

export function Billing() {
  const { query } = useLocation();
  const { refresh } = useSession();
  const [b, setB] = useState<BillingInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [interval, setInterval_] = useState<'month' | 'year'>('month');
  const [busy, setBusy] = useState<string | null>(null);
  const load = () => api<BillingInfo>('/api/billing').then((x) => { setB(x); setError(null); return x; }, (e) => { setError(e.message); return null; });

  useEffect(() => {
    void load();
    // Back from checkout: the plan switches when the payment provider confirms (a few seconds).
    const changed = !!query.get('changed');
    if (query.get('success') || changed) {
      navigate('/billing', { replace: true }); // drop the flag so a remount doesn't repeat this
      toast.success(changed ? 'Plan change requested' : 'Payment received', { description: 'Your plan updates in a few seconds.' });
      let n = 0;
      let start: string | null = null;
      const t = window.setInterval(async () => {
        const x = await load();
        start ??= x?.current.plan ?? null;
        // New payer: done once a subscription exists. Plan change: done once the plan differs from the first poll.
        const done = x && (changed ? n > 0 && x.current.plan !== start : x.current.source === 'subscription');
        if (++n >= 8 || done) { window.clearInterval(t); refresh(); }
      }, 3000);
      return () => window.clearInterval(t);
    }
  }, []);

  async function choose(p: PlanRow) {
    setBusy(p.slug);
    webAnalyticsGoal('checkout_start', { plan: p.slug, interval });
    try {
      const r = await api<{ url: string; changed?: boolean }>('/api/billing/checkout', { method: 'POST', json: { plan: p.slug, interval, attribution: webAnalyticsAttribution() } });
      location.href = r.url;
    } catch (e) {
      toast.error('Could not open checkout', { description: (e as Error).message });
      setBusy(null);
    }
  }
  async function portal() {
    setBusy('portal');
    try {
      location.href = (await api<{ url: string }>('/api/billing/portal', { method: 'POST' })).url;
    } catch (e) {
      toast.error('No billing portal yet', { description: (e as Error).message });
      setBusy(null);
    }
  }

  if (error) return <ErrorState message={`Could not load billing: ${error}`} onRetry={load} />;
  if (!b) return <Spinner label="Loading your plan…" />;
  const saving = (() => {
    const paid = b.plans.filter((p) => p.monthlyCents > 0 && p.yearlyCents > 0);
    const best = Math.max(0, ...paid.map((p) => 1 - p.yearlyCents / (p.monthlyCents * 12)));
    return best > 0.02 ? Math.round(best * 100) : null;
  })();
  const meter = (label: string, used: number, max: number, note: string) => {
    const pct = max ? Math.min(100, (used / max) * 100) : 0;
    return (
      <div className="rounded-2xl border border-border bg-card p-5 shadow-xs">
        <div className="text-xs font-semibold text-muted-foreground">{label}</div>
        <div className="mt-1 text-2xl font-black tabular-nums">{used.toLocaleString()} <span className="text-base font-semibold text-muted-foreground">/ {max.toLocaleString()}</span></div>
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-secondary" role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={max} aria-valuenow={Math.min(used, max)}><div className={cn('h-full rounded-full', pct >= 100 ? 'bg-rose-500' : pct >= 80 ? 'bg-amber-500' : 'bg-primary')} style={{ width: `${pct}%` }} /></div>
        <p className="mt-2 text-[11px] text-muted-foreground">{note}</p>
      </div>
    );
  };
  const sub = b.subscription;
  return (
    <div className="mx-auto max-w-6xl space-y-8">
      <div>
        <h1 className="text-3xl font-extrabold tracking-tight">Subscription & Usage</h1>
        <p className="mt-1 text-sm text-muted-foreground">You're on <b>{b.current.planName}</b>{b.current.source === 'grant' ? ' (given by the team)' : ''}.
          {sub && sub.status !== 'expired' && sub.status !== 'cancelled' && sub.renewsAt && sub.renewsAt > Date.now() ? (sub.cancelsAtPeriodEnd ? ` It ends on ${date(sub.renewsAt)}.` : ` Renews on ${date(sub.renewsAt)} (${sub.interval === 'year' ? 'yearly' : 'monthly'}).`) : ''}
          {sub?.status === 'past_due' ? ' Your last payment failed: update your card in the billing portal.' : ''} Payments are handled securely by our payment provider.</p>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        {meter('Transcription credits', b.usage.credits.used, b.usage.credits.allowance, `Resets on ${date(b.usage.credits.resetsAt)} (your billing date). 1 credit covers up to a minute of reel; transcripts that already exist are free.`)}
        {meter('Connected browsers', b.usage.browsers.used, b.usage.browsers.max, 'Manage them in Settings.')}
        {meter('Profile scans today', b.usage.scansToday.used, b.usage.scansToday.max, 'Resets at midnight UTC.')}
      </div>

      <section className="space-y-4">
        <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
          <h2 className="text-xl font-extrabold">Choose Your Membership</h2>
          <div className="flex items-center gap-2">
            <Segmented value={interval} onChange={setInterval_} options={[{ value: 'month', label: 'Monthly Billing' }, { value: 'year', label: 'Annual Billing' }]} />
            {saving && <Pill tone="emerald">Save up to {saving}%</Pill>}
          </div>
        </div>
        <div className="grid gap-4 lg:grid-cols-3">
          {b.plans.map((p) => {
            const current = b.current.plan === p.slug && (p.slug === 'free' || b.current.source !== 'subscription' || sub?.interval === interval);
            const price = interval === 'year' ? p.yearlyCents : p.monthlyCents;
            const highlight = p.limits?.mcp && p.slug !== 'free';
            return (
              <div key={p.slug} className={cn('relative flex flex-col rounded-3xl border bg-card p-6 shadow-xs', highlight ? 'border-primary shadow-md' : 'border-border', current && 'ring-2 ring-emerald-500/50')}>
                {highlight && <span className="absolute -top-3 left-6 rounded-full bg-primary px-3 py-1 text-[11px] font-bold text-primary-foreground">Most Popular & AI Layer</span>}
                <div className="flex items-center gap-2"><h3 className="text-lg font-extrabold">{p.name}</h3>{current && <Pill tone="emerald">Current plan</Pill>}</div>
                <p className="mt-1 text-xs text-muted-foreground">{p.description}</p>
                <div className="mt-4 text-3xl font-black">{price ? money(price, p.currency) : money(0, p.currency)}<span className="text-sm font-semibold text-muted-foreground">{p.slug === 'free' ? '' : interval === 'year' ? ' / year' : ' / month'}</span></div>
                {p.slug !== 'free' && <p className="text-[11px] text-muted-foreground">+ applicable taxes, added at checkout</p>}
                <ul className="mt-4 flex-1 space-y-2 text-sm">
                  {features(p.limits).map(([f, on]) => <li key={f} className={cn('flex items-start gap-2', !on && 'text-muted-foreground')}>{on ? <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" /> : <X className="mt-0.5 h-4 w-4 shrink-0" />}{f}</li>)}
                </ul>
                <div className="mt-5">
                  {p.slug === 'free' ? <Button variant="secondary" className="w-full" disabled>{current ? 'Your plan' : 'Free forever'}</Button>
                    : current && b.current.source !== 'grant' ? <Button variant="secondary" className="w-full" disabled>Your plan</Button>
                    : !p.purchasable[interval] ? <Button variant="secondary" className="w-full" disabled>Coming soon</Button>
                    : <Button className="w-full" variant={highlight ? 'primary' : 'secondary'} disabled={!!busy} onClick={() => choose(p)}>
                        {busy === p.slug ? <><Loader2 className="h-4 w-4 animate-spin" />Opening checkout…</> : sub && ['active', 'past_due'].includes(sub.status) && !sub.cancelsAtPeriodEnd ? `Switch to ${p.name}` : `Upgrade to ${p.name}`}
                      </Button>}
                </div>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-muted-foreground">Prices exclude taxes. GST, VAT or sales tax for your country is added at checkout by our payment provider and shown on your invoice.</p>
      </section>

      <section className="flex flex-col gap-4 rounded-3xl border border-border bg-card p-6 shadow-xs sm:flex-row sm:items-center">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary"><CreditCard className="h-5 w-5" /></span>
        <div className="min-w-0 flex-1"><h2 className="font-extrabold">Manage Subscription & Invoices</h2>
          <p className="text-sm text-muted-foreground">Update your card, download invoices, or cancel. Cancelling keeps your plan until the end of the period you paid for.</p></div>
        <Button variant="secondary" disabled={!sub?.portal || busy === 'portal'} onClick={portal}>{busy === 'portal' ? 'Opening…' : 'Open billing portal →'}</Button>
      </section>
    </div>
  );
}
